import { hasAnalyticsConsent } from '@/lib/cookie-consent';
import { apiPost, getApiBaseUrl } from '@/services/api-client';
import type { AnalyticsEventName } from '@/types/analytics/events';

const SESSION_KEY = 'xat_analytics_session';
const VISITOR_KEY = 'xat_visitor_id';

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
}

export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return randomId();
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

export function getOrCreateVisitorId(): string {
  if (typeof window === 'undefined') return randomId();
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return randomId();
  }
}

export type IngestEventBody = {
  event_type: AnalyticsEventName | string;
  payload?: Record<string, unknown>;
  session_id?: string;
  visitor_id?: string;
};

/** Ingest público (site); falhas são engolidas para não afetar UX. */
export async function ingestPublicEvent(body: IngestEventBody): Promise<void> {
  if (!hasAnalyticsConsent()) return;
  try {
    const base = getApiBaseUrl();
    await fetch(`${base}/analytics/events`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Visitor-Id': body.visitor_id ?? getOrCreateVisitorId(),
      },
      credentials: 'include',
      body: JSON.stringify({
        event_type: body.event_type,
        payload: body.payload ?? {},
        session_id: body.session_id ?? getOrCreateSessionId(),
        visitor_id: body.visitor_id ?? getOrCreateVisitorId(),
      }),
    });
  } catch {
    /* ignore network errors */
  }
}

/** Ingest autenticado (painel CMS). */
export async function ingestAdminEvent(body: IngestEventBody): Promise<void> {
  try {
    await apiPost(
      '/admin/analytics/events',
      {
        event_type: body.event_type,
        payload: body.payload ?? {},
        session_id: body.session_id ?? getOrCreateSessionId(),
        visitor_id: body.visitor_id ?? getOrCreateVisitorId(),
      },
      true
    );
  } catch {
    /* ignore */
  }
}
